import { UseFormReturn } from "react-hook-form";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProgramSelectionStepProps {
  form: UseFormReturn<any>;
  onNext: () => void;
  onBack: () => void;
}

const programsByType = {
  certificate: [
    { value: "apologetics-cert", label: "Apologetics Certificate" },
    { value: "biblical-counseling-cert", label: "Biblical Counseling Certificate" },
    { value: "chaplain-studies-cert", label: "Chaplain Studies Certificate" },
    { value: "educational-leadership-cert", label: "Educational Leadership Certificate" },
    { value: "evangelism-cert", label: "Evangelism Certificate" },
    { value: "new-testament-studies-cert", label: "New Testament Studies Certificate" },
    { value: "pastoral-care-cert", label: "Pastoral Care Certificate" },
  ],
  associate: [
    { value: "biblical-studies-aa", label: "Associate of Arts in Biblical Studies" },
    { value: "ministry-aa", label: "Associate of Arts in Ministry" },
  ],
  bachelor: [
    { value: "biblical-studies-ba", label: "B.A. in Biblical Studies" },
    { value: "christian-counseling-ba", label: "B.A. in Christian Counseling" },
    { value: "christian-education-ba", label: "B.A. in Christian Education" },
    { value: "church-history-ba", label: "B.A. in Church History" },
    { value: "divinity-ba", label: "B.A. in Divinity" },
    { value: "ministry-studies-ba", label: "B.A. in Ministry Studies" },
    { value: "philosophy-ba", label: "B.A. in Philosophy" },
    { value: "christian-psychology-ba", label: "B.A. in Christian Psychology" },
    { value: "theology-ba", label: "B.A. in Theology" },
  ],
  master: [
    { value: "biblical-studies-ma", label: "M.A. in Biblical Studies" },
    { value: "christian-counseling-ma", label: "M.A. in Christian Counseling" },
    { value: "christian-education-ma", label: "M.A. in Christian Education" },
    { value: "church-history-ma", label: "M.A. in Church History" },
    { value: "divinity-mdiv", label: "Master of Divinity (M.Div.)" },
    { value: "theology-apologetics-ma", label: "M.A. in Theology and Apologetics" },
    { value: "ministry-studies-ma", label: "M.A. in Ministry Studies" },
    { value: "philosophy-ma", label: "M.A. in Philosophy" },
    { value: "christian-psychology-ma", label: "M.A. in Christian Psychology" },
    { value: "theology-ma", label: "M.A. in Theology" },
  ],
  doctorate: [
    // Doctorate Degrees
    { value: "biblical-studies-bsd", label: "B.S.D Biblical Study" },
    { value: "christian-counseling-dcc", label: "D.C.C Christian Counseling" },
    { value: "christian-education-dced", label: "D.C.Ed Christian Education" },
    { value: "church-history-chd", label: "C.H.D Church History" },
    { value: "divinity-ddiv", label: "D.Div. Divinity" },
    { value: "ministry-dmin", label: "D.Min. Ministry" },
    { value: "theology-thd", label: "Th.D. Theology" },
    // PhD Programs
    { value: "biblical-studies-phd", label: "Ph.D. Biblical Study" },
    { value: "christian-counseling-phd", label: "Ph.D. Christian Counseling" },
    { value: "christian-education-phd", label: "Ph.D. Christian Education" },
    { value: "church-history-phd", label: "Ph.D. Church History" },
    { value: "divinity-phd", label: "Ph.D. Divinity" },
    { value: "ministry-phd", label: "Ph.D. Ministry" },
    { value: "theology-phd", label: "Ph.D. Theology" },
  ],
};

export const ProgramSelectionStep = ({ form, onNext, onBack }: ProgramSelectionStepProps) => {
  const programInterest = form.watch("programInterest");
  const availablePrograms = programInterest ? programsByType[programInterest as keyof typeof programsByType] || [] : [];

  return (
    <Form {...form}>
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">Program Selection</h3>
      
      <FormField
        control={form.control}
        name="programInterest"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Program Level *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select program level" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="certificate">Certificate</SelectItem>
                <SelectItem value="associate">Associate Degree</SelectItem>
                <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                <SelectItem value="master">Master's Degree</SelectItem>
                <SelectItem value="doctorate">Doctorate</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      {availablePrograms.length > 0 && (
        <FormField
          control={form.control}
          name="specificProgram"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Specific Program</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a program" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {availablePrograms.map((program) => (
                    <SelectItem key={program.value} value={program.value}>
                      {program.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
      )}

      <FormField
        control={form.control}
        name="intendedStartDate"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Intended Start Date *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select start term" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="fall-2025">Fall 2025</SelectItem>
                <SelectItem value="spring-2026">Spring 2026</SelectItem>
                <SelectItem value="summer-2026">Summer 2026</SelectItem>
                <SelectItem value="fall-2026">Fall 2026</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="enrollmentStatus"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Enrollment Status *</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Select enrollment status" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="full-time">Full-time</SelectItem>
                <SelectItem value="part-time">Part-time</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage />
          </FormItem>
        )}
      />

        <div className="flex justify-between pt-4">
          <Button variant="outline" onClick={onBack}>Back</Button>
          <Button onClick={onNext}>Next</Button>
        </div>
      </div>
    </Form>
  );
};
